import { create } from 'zustand';
import { executorAddressService } from '../api/executorAddressService';
import { getErrorMessage } from '@/shared/lib/errors';
import { RequestStatus } from '@/shared/model/requestStatus';
import type { ExecutorAddressData, ExecutorAddressState } from './types';

const initialState: ExecutorAddressData = {
    executorAddresses: [],
    executorAddress: null,
    loading: false,
    listStatus: RequestStatus.Idle,
    listError: null,
    listExecutorId: null,
    detailsStatus: RequestStatus.Idle,
    detailsError: null,
};

let latestListRequest = 0;
let latestDetailsRequest = 0;

/** Хранит адреса исполнителя и состояние их загрузки */
export const useExecutorAddresses = create<ExecutorAddressState>()((set) => ({
    ...initialState,

    getAllExecutorAddresses: async (executorId, filters) => {
        const requestId = ++latestListRequest;

        set({
            executorAddresses: [],
            listExecutorId: executorId,
            listStatus: RequestStatus.Loading,
            listError: null,
            loading: true,
        });

        try {
            const addresses = await executorAddressService.getAllExecutorAddresses(executorId, filters);

            if (requestId !== latestListRequest) return;

            set({
                executorAddresses: addresses,
                listStatus: RequestStatus.Success
            });
        } catch (error: unknown) {
            if (requestId !== latestListRequest) return;

            set({
                executorAddresses: [],
                listStatus: RequestStatus.Error,
                listError: getErrorMessage(error, 'Не удалось загрузить адреса исполнителя'),
            });
        } finally {
            if (requestId === latestListRequest) set({ loading: false });
        }
    },

    getExecutorAddressById: async (id) => {
        const requestId = ++latestDetailsRequest;

        set({
            executorAddress: null,
            detailsStatus: RequestStatus.Loading,
            detailsError: null
        });

        try {
            const address = await executorAddressService.getExecutorAddressById(id);

            if (requestId !== latestDetailsRequest) return;

            set({
                executorAddress: address,
                detailsStatus: RequestStatus.Success
            });
        } catch (error: unknown) {
            if (requestId !== latestDetailsRequest) return;

            set({
                detailsStatus: RequestStatus.Error,
                detailsError: getErrorMessage(error, 'Не удалось загрузить адрес исполнителя'),
            });
        }
    },

    deactivateExecutorAddress: async (id, comment) => {
        await executorAddressService.deleteExecutorAddress(id, comment);

        set((state) => ({
            executorAddresses: state.executorAddresses.filter((address) => address.id !== id),
            executorAddress: state.executorAddress?.id === id
                ? null
                : state.executorAddress,
        }));
    },

    setFavorite: async (id) => {
        const listRequest = latestListRequest;

        try {
            await executorAddressService.setFavorite(id);

            set((state) => ({
                executorAddresses: listRequest === latestListRequest
                    ? state.executorAddresses.map((address) => ({
                        ...address,
                        isFavorite: address.id === id,
                    }))
                    : state.executorAddresses,
                executorAddress: state.executorAddress?.id === id
                    ? {
                        ...state.executorAddress,
                        isFavorite: true
                    }
                    : state.executorAddress,
            }));

            return true;
        } catch {
            return false;
        }
    },

    clearCurrent: () => {
        latestDetailsRequest += 1;

        set({
            executorAddress: null,
            detailsStatus: RequestStatus.Idle,
            detailsError: null
        });
    },

    clearAddresses: () => {
        latestListRequest += 1;

        set({
            executorAddresses: [],
            listExecutorId: null,
            listStatus: RequestStatus.Idle,
            listError: null,
            loading: false,
        });
    },
}));
